import { getRealtimeBaseUrl } from "../realtimeBaseUrl";

/**
 * KeyManager - مدير المفاتيح
 * يدير دورة حياة مفاتيح AES و RSA
 */
class KeyManager {
  constructor() {
    this.cachedAesKey = null;
    this.cachedPublicKey = null;
    this.cachedPublicKeyVersion = null;
  }

  /**
   * التحقق من أننا في بيئة المتصفح
   * @returns {boolean}
   */
  isBrowser() {
    return typeof window !== "undefined" && typeof window.crypto !== "undefined";
  }

  /**
   * الحصول على مفتاح AES للجلسة (32 حرف ASCII = 32 بايت UTF-8)
   * السيرفر يتعامل مع AES key كـ string ويفرض طول 16/24/32 بايت بعد UTF-8.
   * @returns {Promise<string>} مفتاح AES كنص بطول 32
   */
  async getAesKey() {
    // التحقق من بيئة المتصفح
    if (!this.isBrowser()) {
      throw new Error("AES key generation is only available in browser environment");
    }

    // 1) ذاكرة (أسرع)
    if (this.cachedAesKey) return this.cachedAesKey;

    // 2) sessionStorage (مفتاح لكل جلسة تبويب)
    try {
      const stored = sessionStorage.getItem("ecoAesKey");
      if (stored && stored.length === 32) {
        this.cachedAesKey = stored;
        return stored;
      }
    } catch (error) {
      // sessionStorage غير متاح (مثل وضع الخصوصية)
      console.warn("sessionStorage not available:", error);
    }

    // 3) توليد مفتاح نصي 32 حرف (ASCII) لضمان 32 bytes بعد UTF-8
    const aesKey = this.generateAesKeyString(32);
    try {
      sessionStorage.setItem("ecoAesKey", aesKey);
    } catch (error) {
      // sessionStorage غير متاح، نكتفي بالذاكرة فقط
      console.warn("Failed to save AES key to sessionStorage:", error);
    }
    this.cachedAesKey = aesKey;
    return aesKey;
  }

  /**
   * جلب المفتاح العام RSA من السيرفر
   * @returns {Promise<string>} المفتاح العام RSA (PEM format)
   */
  async fetchPublicKeyFromServer() {
    // التحقق من بيئة المتصفح
    if (!this.isBrowser()) {
      throw new Error("Public key fetching is only available in browser environment");
    }

    const socketUrl = getRealtimeBaseUrl();
    if (!socketUrl) {
      throw new Error("VITE_SOCKET_URL is not defined");
    }

    try {
      const response = await fetch(`${socketUrl}/secret/public-key`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch public key: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (!data || !data.key) {
        throw new Error("Invalid public key response from server");
      }

      this.cachedPublicKey = data.key;
      this.cachedPublicKeyVersion = data.version || null;

      // حفظ في localStorage
      try {
        if (data.key) {
          localStorage.setItem("rsaPublicKey", data.key);
        }
        if (data.version) {
          localStorage.setItem("rsaPublicKeyVersion", data.version);
        }
      } catch (storageError) {
        // localStorage غير متاح (مثل وضع الخصوصية)
        console.warn("Failed to save public key to localStorage:", storageError);
      }

      return data.key;
    } catch (error) {
      // تحسين رسالة الخطأ
      if (error.message.includes("fetch")) {
        throw new Error(`Network error while fetching public key: ${error.message}`);
      }
      throw error;
    }
  }

  /**
   * جلب المفتاح العام (من الذاكرة أو التخزين أو السيرفر)
   * @param {boolean} forceRefresh - إجبار تحديث المفتاح
   * @returns {Promise<string>} المفتاح العام RSA
   */
  async getPublicKey(forceRefresh = false) {
    // التحقق من بيئة المتصفح
    if (!this.isBrowser()) {
      throw new Error("Public key retrieval is only available in browser environment");
    }

    if (this.cachedPublicKey && !forceRefresh) {
      return this.cachedPublicKey;
    }

    // محاولة جلب من localStorage
    try {
      const storedKey = localStorage.getItem("rsaPublicKey");
      if (storedKey && !forceRefresh) {
        this.cachedPublicKey = storedKey;
        return storedKey;
      }
    } catch (error) {
      // localStorage غير متاح
      console.warn("localStorage not available:", error);
    }

    // جلب من السيرفر
    return await this.fetchPublicKeyFromServer();
  }

  /**
   * جلب إصدار المفتاح العام
   * @returns {Promise<string|null>} إصدار المفتاح
   */
  async getPublicKeyVersion() {
    // التحقق من بيئة المتصفح
    if (!this.isBrowser()) {
      return null;
    }

    if (this.cachedPublicKeyVersion) {
      return this.cachedPublicKeyVersion;
    }

    try {
      const version = localStorage.getItem("rsaPublicKeyVersion");
      if (version) {
        this.cachedPublicKeyVersion = version;
        return version;
      }
    } catch (error) {
      // localStorage غير متاح
      console.warn("localStorage not available:", error);
    }

    return null;
  }

  /**
   * مسح جميع المفاتيح (عند تسجيل الخروج)
   */
  clearAllKeys() {
    this.cachedAesKey = null;
    this.cachedPublicKey = null;
    this.cachedPublicKeyVersion = null;
    
    if (this.isBrowser()) {
      try {
        sessionStorage.removeItem("ecoAesKey");
        localStorage.removeItem("rsaPublicKey");
        localStorage.removeItem("rsaPublicKeyVersion");
      } catch (error) {
        console.warn("Failed to clear keys from storage:", error);
      }
    }
  }

  /**
   * إعادة تعيين مفتاح AES (لإنشاء مفتاح جديد)
   */
  resetAesKey() {
    this.cachedAesKey = null;
    
    if (this.isBrowser()) {
      try {
        sessionStorage.removeItem("ecoAesKey");
      } catch (error) {
        console.warn("Failed to reset AES key in storage:", error);
      }
    }
  }

  /**
   * توليد مفتاح AES كنص ASCII بطول محدد (افتراضياً 32)
   * @param {number} length
   * @returns {string}
   */
  generateAesKeyString(length = 32) {
    // التحقق من بيئة المتصفح
    if (!this.isBrowser()) {
      throw new Error("AES key generation requires browser crypto API");
    }

    // أحرف ASCII آمنة (1 byte لكل حرف)
    const charset =
      "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/";
    const random = new Uint8Array(length);
    
    try {
      crypto.getRandomValues(random);
    } catch (error) {
      throw new Error(`Failed to generate random values: ${error.message}`);
    }
    
    let out = "";
    for (let i = 0; i < length; i++) {
      out += charset[random[i] % charset.length];
    }
    return out;
  }
}

// Export singleton instance
export const keyManager = new KeyManager();
export default keyManager;
